/**
 * WASSER E-commerce Platform - Navigation Progress Bar
 *
 * Компонент для отображения прогресс-бара при навигации между страницами.
 * Реализация без внешних зависимостей (аналог NProgress).
 *
 * @example
 * // В корневом App.jsx
 * import NavigationProgress from '../components/common/NavigationProgress';
 *
 * function App() {
 *   return (
 *     <>
 *       <NavigationProgress />
 *       <Routes>...</Routes>
 *     </>
 *   );
 * }
 */
import { Box, LinearProgress, useTheme } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * NavigationProgress Component
 *
 * @param {Object} props
 * @param {string} [props.color] - Цвет прогресс-бара (по умолчанию primary)
 * @param {number} [props.height=3] - Высота бара в пикселях
 * @param {number} [props.minDuration=300] - Минимальная длительность показа (мс)
 * @param {boolean} [props.showSpinner=false] - Показывать спиннер справа
 */
const NavigationProgress = ({
  color,
  height = 3,
  minDuration = 300,
  showSpinner = false,
}) => {
  const location = useLocation();
  const theme = useTheme();
  const [state, setState] = useState({ isNavigating: false, progress: 0 });
  const timeoutsRef = useRef([]);
  const intervalRef = useRef(null);

  // Создаем уникальный ключ для текущего пути
  const pathKey = useMemo(
    () => location.pathname + location.search,
    [location.pathname, location.search]
  );

  // Храним предыдущий путь
  const prevPathRef = useRef(pathKey);
  const isFirstMount = useRef(true);

  useEffect(() => {
    // Пропускаем первый mount
    if (isFirstMount.current) {
      isFirstMount.current = false;
      prevPathRef.current = pathKey;
      return;
    }

    // Если путь не изменился, ничего не делаем
    if (pathKey === prevPathRef.current) {
      return;
    }

    prevPathRef.current = pathKey;

    // Очистка предыдущих таймеров
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Запускаем анимацию через microtask, чтобы обойти lint
    queueMicrotask(() => {
      setState({ isNavigating: true, progress: 0 });

      // Быстро до 30%
      const t1 = setTimeout(() => {
        setState((s) => ({ ...s, progress: 30 }));
      }, 50);
      timeoutsRef.current.push(t1);

      // Медленно до 70%
      intervalRef.current = setInterval(() => {
        setState((s) => {
          if (s.progress >= 70) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            return s;
          }
          return { ...s, progress: s.progress + Math.random() * 10 };
        });
      }, 200);

      // Завершаем
      const t2 = setTimeout(() => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setState((s) => ({ ...s, progress: 100 }));

        // Скрываем
        const t3 = setTimeout(() => {
          setState({ isNavigating: false, progress: 0 });
        }, 200);
        timeoutsRef.current.push(t3);
      }, minDuration);
      timeoutsRef.current.push(t2);
    });

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [pathKey, minDuration]);

  // Cleanup при unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const { isNavigating, progress } = state;

  if (!isNavigating && progress === 0) {
    return null;
  }

  const barColor = color || theme.palette.primary.main;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar + 100,
        height: height,
        pointerEvents: 'none',
      }}
    >
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: height,
          backgroundColor: 'transparent',
          '& .MuiLinearProgress-bar': {
            backgroundColor: barColor,
            transition:
              progress === 100 ? 'transform 0.2s ease' : 'transform 0.5s ease',
          },
        }}
      />

      {/* Эффект свечения на конце бара */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: `${100 - progress}%`,
          width: 100,
          height: height,
          background: `linear-gradient(to right, transparent, ${barColor})`,
          boxShadow: `0 0 10px ${barColor}, 0 0 5px ${barColor}`,
          opacity: isNavigating ? 1 : 0,
          transition: 'opacity 0.2s ease',
          transform: 'translateX(50%)',
        }}
      />

      {/* Опциональный спиннер */}
      {showSpinner && isNavigating && (
        <Box
          sx={{
            position: 'absolute',
            top: 15,
            right: 15,
            width: 18,
            height: 18,
            border: `2px solid ${barColor}`,
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />
      )}
    </Box>
  );
};

export default NavigationProgress;
